import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/hooks/useCartSync';
import { useCurrency } from '@/hooks/useCurrency';
import DOMPurify from 'dompurify';
import { useToast } from '@/hooks/use-toast';
import { formatPriceWithCurrency } from '@/utils/currency';
import { ShoppingCart, Percent, Clock, Package } from 'lucide-react';

// Helper function to sanitize HTML content
const sanitizeHTML = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html);
};

interface Product {
  id: string;
  title: string;
  description: string;
  price_cents: number;
  currency: string;
  images: string[];
  stock: number;
  category: string;
}

interface Advertisement {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  promotion_type: string;
  category_filter: string | null;
  discount_percentage: number;
  auto_apply_discount: boolean;
}

export default function PromotionPage() {
  const { adId } = useParams<{ adId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addItem } = useCart();
  const { displayCurrency, convertPrice } = useCurrency();
  
  const [ad, setAd] = useState<Advertisement | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const calculateDiscountedPrice = (originalPrice: number) => {
    if (!ad || !ad.auto_apply_discount) return originalPrice;
    return Math.round(originalPrice * (1 - ad.discount_percentage / 100));
  };

  useEffect(() => {
    if (adId) {
      fetchPromotion();
    }
  }, [adId]);

  useEffect(() => {
    if (ad) {
      fetchProducts();
    }
  }, [ad]);

  const fetchPromotion = async () => {
    try {
      const { data, error } = await supabase
        .from('advertisements')
        .select('id, title, description, image_url, promotion_type, category_filter, discount_percentage, auto_apply_discount')
        .eq('id', adId)
        .eq('active', true)
        .lte('start_date', new Date().toISOString())
        .gte('end_date', new Date().toISOString())
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast({
          title: 'Erreur',
          description: 'Promotion introuvable ou expirée',
          variant: 'destructive'
        });
        navigate('/');
        return;
      }
      
      setAd(data as unknown as Advertisement);
    } catch (error) {
      console.error('Error fetching promotion:', error);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement de la promotion',
        variant: 'destructive'
      });
      navigate('/');
    }
  };

  const fetchProducts = async () => {
    if (!ad) return;
    
    try {
      let query = supabase
        .from('products')
        .select('*')
        .eq('active', true)
        .gt('stock', 0);

      if (ad.promotion_type === 'category' && ad.category_filter) {
        query = query.eq('category', ad.category_filter);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Removed formatPrice function - using formatPriceWithCurrency instead

  const handleAddToCart = (product: Product) => {
    addItem({
      id: product.id,
      title: product.title,
      price_cents: product.price_cents,
      currency: product.currency,
      images: product.images,
      vendor_id: '',
    }, 1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Chargement de la promotion...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Promotion introuvable</h2>
            <p className="text-muted-foreground mb-4">Cette promotion n'existe pas ou a expiré</p>
            <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      {/* Amazon-style Hero Banner */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-glow to-secondary opacity-95"></div>
        {ad.image_url && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-20" 
            style={{ backgroundImage: `url(${ad.image_url})` }}
          ></div>
        )}
        
        <div className="relative container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl">
            {/* Discount Badge - Amazon style */}
            <div className="inline-flex items-center gap-2 bg-destructive text-destructive-foreground px-6 py-3 rounded-full text-2xl md:text-3xl font-bold mb-6 shadow-lg animate-fade-in">
              <Percent className="w-8 h-8" />
              Économisez {ad.discount_percentage}%
            </div>
            
            {/* Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-4 leading-tight">
              {ad.title}
            </h1>
            
            {/* Description */}
            {ad.description && (
              <div 
                className="text-xl md:text-2xl text-primary-foreground/90 mb-8 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(ad.description) }}
              />
            )}
            
            {/* CTA */}
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold text-lg px-8 py-6 shadow-xl"
                onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Découvrir les offres
              </Button>
              <div className="flex items-center gap-2 bg-background/20 backdrop-blur-sm text-primary-foreground px-6 py-3 rounded-lg">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-semibold">Offre à durée limitée</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlight Bar */}
      <section className="bg-accent border-y border-border">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                <Percent className="w-5 h-5 text-success" />
              </div>
              <div className="text-left">
                <p className="font-bold text-foreground">Réduction automatique</p>
                <p className="text-sm text-muted-foreground">Prix déjà réduits</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-success" />
              </div>
              <div className="text-left">
                <p className="font-bold text-foreground">{products.length} produits</p>
                <p className="text-sm text-muted-foreground">En promotion</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 md:py-16 px-4 flex-1 bg-muted/30">
        <div className="container mx-auto">
          {/* Section Header */}
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Produits en promotion
            </h2>
            <p className="text-muted-foreground text-lg">
              Profitez de {ad.discount_percentage}% de réduction sur tous ces produits
            </p>
          </div>
          
          {products.length === 0 ? (
            <div className="text-center py-16 bg-card rounded-xl shadow-sm">
              <p className="text-muted-foreground text-lg">Aucun produit disponible pour cette promotion</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => {
                const originalPrice = product.price_cents;
                // Always calculate discount if promotion exists
                const discountedPrice = ad.discount_percentage > 0 
                  ? Math.round(originalPrice * (1 - ad.discount_percentage / 100))
                  : originalPrice;
                const hasDiscount = ad.discount_percentage > 0 && discountedPrice !== originalPrice;
                const savings = hasDiscount ? originalPrice - discountedPrice : 0;

                return (
                  <div 
                    key={product.id} 
                    className="group bg-card rounded-xl shadow-card hover:shadow-elegant transition-all duration-300 overflow-hidden border border-border hover:border-primary/20"
                  >
                    {/* Product Image */}
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      {hasDiscount && (
                        <div className="absolute top-3 left-3 z-10">
                          <Badge className="bg-destructive text-destructive-foreground font-bold text-base px-3 py-1 shadow-lg">
                            -{ad.discount_percentage}%
                          </Badge>
                        </div>
                      )}
                      <img
                        src={product.images[0] || '/placeholder.svg'}
                        alt={product.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>

                    {/* Product Info */}
                    <div className="p-5">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2 text-foreground group-hover:text-primary transition-colors">
                        {product.title}
                      </h3>
                      {product.description && (
                        <div 
                          className="text-sm text-muted-foreground mb-4 line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: sanitizeHTML(product.description) }}
                        />
                      )}

                      {/* Price Section - Always show discount on promotion page */}
                      <div className="mb-5">
                        {ad && ad.discount_percentage > 0 ? (
                          <div className="space-y-1">
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-bold text-destructive">
                                {formatPriceWithCurrency(discountedPrice, product.currency, displayCurrency, convertPrice)}
                              </span>
                              <Badge variant="destructive" className="text-sm font-bold">
                                -{ad.discount_percentage}%
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-base text-muted-foreground line-through">
                                {formatPriceWithCurrency(originalPrice, product.currency, displayCurrency, convertPrice)}
                              </span>
                              {savings > 0 && (
                                <span className="text-sm font-semibold text-success">
                                  • Économisez {formatPriceWithCurrency(savings, product.currency, displayCurrency, convertPrice)}
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-3xl font-bold text-foreground">
                            {formatPriceWithCurrency(originalPrice, product.currency, displayCurrency, convertPrice)}
                          </span>
                        )}
                      </div>

                      {/* Stock indicator */}
                      {product.stock < 10 && (
                        <p className="text-sm text-destructive font-semibold mb-3">
                          Plus que {product.stock} en stock !
                        </p>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleAddToCart(product)}
                          className="flex-1 font-semibold shadow-sm hover:shadow-md"
                          size="lg"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Ajouter
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => navigate(`/product/${product.id}`)}
                          size="lg"
                          className="hover:bg-accent"
                        >
                          Voir
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
